import { useMemo } from 'react'
import isNumber from 'lodash/isNumber'
import toInteger from 'lodash/toInteger'
import { PaymentNetworkOptions, PAYMENT_ACCOUNT_TYPE_PLACEHOLDER } from '@klear/payment/constants'
import { ExternalLink } from 'lucide-react'
import { EventFieldGrid, type EventField } from '@klear/order-event-ui/event-forms/event-field-grid'
import { Alert, AlertDescription } from '@klear/ui/components/alert'
import { useKlearFinancialAccount } from '@klear/ui/hooks/use-klear-financial-account'
import { useLinkedPayment } from '@klear/ui/hooks/use-linked-payments'
import { useSupplierExternalBankAccounts } from '@klear/ui/hooks/use-supplier-external-bank-accounts'
import { materializePaymentViewModel, type PaymentViewSource } from '@klear/order-event-ui/payment-view-source'
import { dateFormatted, type DateDisplayInput } from '@klear/order-event/list-display'

type PaymentViewRow = {
  number?: string
  effectiveDate?: DateDisplayInput
  paymentNetwork?: string
  destinationAccountId?: number
  amountFormatted?: string | null
  paymentStatus?: string
  stripeReceiptUrl?: string
}

function paymentViewFields(
  ctx: {
    klearFinancialAccountLabel?: string
    externalBankAccounts: ReturnType<typeof useSupplierExternalBankAccounts>['supplierExternalBankAccounts']
    linkedPayment?: { status?: string; receiptUrl?: string | null } | null
  },
  row: PaymentViewRow,
  options?: { includeNumber?: boolean }
): EventField[] {
  const receiptUrl = ctx.linkedPayment?.receiptUrl ?? row.stripeReceiptUrl
  const fields: EventField[] = [
    { label: 'Payment Date', value: dateFormatted(row.effectiveDate) },
    {
      label: 'Network',
      value: PaymentNetworkOptions.find((network) => network.value === row.paymentNetwork)?.label ?? null
    },
    { label: 'Source Account', value: ctx.klearFinancialAccountLabel ?? null },
    {
      label: 'Destination Account',
      value: ctx.externalBankAccounts.find((account) => account.id === row.destinationAccountId)?.nameAndLast4 ?? null
    },
    { label: 'Status', value: ctx.linkedPayment?.status ?? row.paymentStatus ?? null },
    { label: 'Amount', value: row.amountFormatted ?? null },
    {
      label: 'Receipt',
      value: receiptUrl ? (
        <a href={receiptUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
          View Receipt
          <ExternalLink className="w-4 h-4" />
        </a>
      ) : null
    }
  ]

  if (options?.includeNumber) {
    fields.unshift({ label: 'Number', value: row.number ?? null })
  }

  return fields
}

type PaymentViewProps = {
  payment: PaymentViewSource
  parentSupplierId?: number | string
}

export function PaymentView({ payment, parentSupplierId }: PaymentViewProps) {
  const viewModel = useMemo(() => materializePaymentViewModel(payment), [payment])
  const { payment: domainPayment, number, linkedPayableId, displayStatus } = viewModel

  const supplierId =
    parentSupplierId == null ? undefined : isNumber(parentSupplierId) ? parentSupplierId : toInteger(parentSupplierId)
  const { accounts, receivableAccount } = useKlearFinancialAccount()
  const sourceKlearAccount =
    domainPayment.sourceAccountType === PAYMENT_ACCOUNT_TYPE_PLACEHOLDER
      ? undefined
      : (accounts.find((a) => a.id === domainPayment.sourceAccountId) ?? receivableAccount ?? accounts[0])
  const { supplierExternalBankAccounts: externalBankAccounts } = useSupplierExternalBankAccounts(supplierId)
  const { linkedPayment } = useLinkedPayment(linkedPayableId)
  const stripePaymentErrorMessage = linkedPayment?.stripePaymentErrorMessage

  const row: PaymentViewRow = {
    number,
    effectiveDate: domainPayment.effectiveDate,
    paymentNetwork: domainPayment.paymentNetwork,
    destinationAccountId: domainPayment.destinationAccountId,
    amountFormatted: domainPayment.amount.toString(),
    paymentStatus: displayStatus,
    stripeReceiptUrl: undefined
  }

  return (
    <>
      {stripePaymentErrorMessage ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{stripePaymentErrorMessage}</AlertDescription>
        </Alert>
      ) : null}
      <EventFieldGrid
        fields={paymentViewFields(
          {
            klearFinancialAccountLabel: sourceKlearAccount?.nameAndLast4,
            externalBankAccounts,
            linkedPayment
          },
          row,
          { includeNumber: number != null }
        )}
      />
    </>
  )
}
